import { format } from 'd3-format';

/**
 * Definition for global options
 */
export interface GlobalOptionsType {
  /** Price formatter function */
  priceFormatter: (value: number) => string;
  /** Volume formatter function */
  volumeFormatter: (value: number) => string;
  /** Percent formatter function */
  percentFormatter: (value: number) => string;
}

/**
 * 숫자 크기에 따라 적절한 소수점 자릿수를 결정
 * @param value 포맷할 숫자
 * @returns 소수점 자릿수
 */
function getAutoFractionDigits(value: number): number {
  const absValue = Math.abs(value);
  if (absValue < 1) return 7;
  if (absValue < 10) return 6;
  if (absValue < 100) return 5;
  if (absValue < 1000) return 4;
  if (absValue < 10000) return 3;
  return 2;
}

/**
 * 숫자 크기에 따라 자동으로 소수점 자릿수를 조절하는 포맷터
 * @param value 포맷할 숫자
 * @returns 포맷된 문자열
 */
function autoFractionPriceFormatter(value: number): string {
  const fractionDigits = getAutoFractionDigits(value);
  return format(`.${fractionDigits}f`)(value);
}

/**
 * Default values
 */
const defaults: GlobalOptionsType = {
  priceFormatter: autoFractionPriceFormatter,
  volumeFormatter: format('.2s'),
  percentFormatter: format('.2%'),
};
let userOptions: Partial<GlobalOptionsType> = {};

/**
 *  Proxy to return user options if available, otherwise default values
 */
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
export const globalOptions: GlobalOptionsType = new Proxy({} as GlobalOptionsType, {
  get(_target, key: keyof GlobalOptionsType) {
    return userOptions[key] ?? defaults[key];
  },
});

/**
 * Set global options
 * Called once when app starts from outside, e.g. engview's _app.tsx
 *
 * @example
 * // engview's _app.tsx
 * import { setGlobalOptions } from '@bit-box/chart-tools';
 * import { priceFormatter } from './common/format';
 *
 * setGlobalOptions({
 *   priceFormatter: priceFormatter,
 * });
 */
export function setGlobalOptions(options: Partial<GlobalOptionsType>): void {
  userOptions = { ...userOptions, ...options };
}

/**
 * Reset global options (for testing)
 */
export function resetGlobalOptions(): void {
  userOptions = {};
}
